import { useCallback, useEffect, useState } from "react"
import {
  listCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  type CalendarEventRow,
  type CalendarEventInput,
} from "@/lib/supabase"

/**
 * Hook qui gère le CRUD des événements calendrier pour un mois donné.
 * Recharge automatiquement quand monthStart/monthEnd changent.
 */
export function useCalendarEvents(monthStart: string, monthEnd: string) {
  const [events, setEvents] = useState<CalendarEventRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listCalendarEvents(monthStart, monthEnd)
      setEvents(data)
    } catch (err) {
      console.error("[useCalendarEvents] fetch error:", err)
      setError(err instanceof Error ? err.message : "Erreur chargement")
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [monthStart, monthEnd])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const addEvent = useCallback(
    async (input: CalendarEventInput): Promise<{ error: string | null; event: CalendarEventRow | null }> => {
      try {
        const newEvent = await createCalendarEvent(input)
        setEvents((prev) => [...prev, newEvent].sort((a, b) => a.event_date.localeCompare(b.event_date)))
        return { error: null, event: newEvent }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur création"
        console.error("[useCalendarEvents] add error:", err)
        return { error: msg, event: null }
      }
    },
    []
  )

  const editEvent = useCallback(
    async (
      id: string,
      updates: Partial<CalendarEventInput>
    ): Promise<{ error: string | null; event: CalendarEventRow | null }> => {
      try {
        const updated = await updateCalendarEvent(id, updates)
        setEvents((prev) =>
          prev
            .map((e) => (e.id === id ? updated : e))
            .sort((a, b) => a.event_date.localeCompare(b.event_date))
        )
        return { error: null, event: updated }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur mise à jour"
        console.error("[useCalendarEvents] edit error:", err)
        return { error: msg, event: null }
      }
    },
    []
  )

  const removeEvent = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      try {
        await deleteCalendarEvent(id)
        setEvents((prev) => prev.filter((e) => e.id !== id))
        return { error: null }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur suppression"
        console.error("[useCalendarEvents] remove error:", err)
        return { error: msg }
      }
    },
    []
  )

  return {
    events,
    loading,
    error,
    refresh: fetchEvents,
    addEvent,
    editEvent,
    removeEvent,
  }
}

// ============================================
// HELPERS
// ============================================

/**
 * Retourne la première et dernière date du mois d'une date donnée, au format YYYY-MM-DD.
 */
export function getMonthBounds(date: Date): { start: string; end: string } {
  const year = date.getFullYear()
  const month = date.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const toISO = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  }
  return {
    start: toISO(firstDay),
    end: toISO(lastDay),
  }
}

/**
 * Génère les 42 cellules (6 semaines) de la grille mensuelle, démarrant lundi.
 */
export interface CalendarCell {
  date: Date
  iso: string         // YYYY-MM-DD
  isCurrentMonth: boolean
  isToday: boolean
  dayNumber: number
}

export function getMonthGrid(referenceDate: Date): CalendarCell[] {
  const year = referenceDate.getFullYear()
  const month = referenceDate.getMonth()
  const firstDayOfMonth = new Date(year, month, 1)

  // JS: getDay() → 0=dimanche, 1=lundi… On veut lundi=0
  const firstDayJs = firstDayOfMonth.getDay()
  const firstDayShift = (firstDayJs + 6) % 7 // 0=lundi, 6=dimanche

  const gridStart = new Date(year, month, 1 - firstDayShift)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const cells: CalendarCell[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    cells.push({
      date: d,
      iso: `${y}-${m}-${day}`,
      isCurrentMonth: d.getMonth() === month,
      isToday: d.getTime() === today.getTime(),
      dayNumber: d.getDate(),
    })
  }
  return cells
}

/**
 * Nom du mois en français.
 */
export function getMonthLabel(date: Date): string {
  return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    .replace(/^./, (s) => s.toUpperCase())
}

/**
 * Indique si iso est la date d'aujourd'hui.
 */
export function isDateToday(iso: string): boolean {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, "0")
  const d = String(today.getDate()).padStart(2, "0")
  return iso === `${y}-${m}-${d}`
}
